import { useState } from "react";
import { Link } from "react-router-dom";
import { CButton, CCard, CCardBody, CCardHeader, CCol, CFormInput, CSpinner } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilLockLocked } from "@coreui/icons";
import {
  MAX_PLATFORM_ACCOUNT_LENGTH,
  type PlatformAccountDto,
  usePlatformAccounts,
  useRemovePlatformAccount,
  useSetPlatformAccount,
} from "../../api/hooks/usePlatformAccounts";
import { useMembershipsEnabled } from "../../api/hooks/usePortalSettings";
import { useTradingViewAccount } from "../../api/hooks/useTradingViewAccount";
import { useToast } from "../../context/useToast";
import { routePaths } from "../../routes/route-paths";
import { MembershipBadge } from "../trading-strategies/components/MembershipBadge";

/**
 * The customer's TradingView username, which the TradingView add-on invites, then their account on each platform
 * we take memberships from. Telling us about a membership fills the account in from here, and saves to it.
 */
export function PlatformAccountsCard() {
  const membershipsEnabled = useMembershipsEnabled();

  return (
    <CCol md={6}>
      <CCard className="h-100">
        <CCardHeader>Platform accounts</CCardHeader>
        <CCardBody className="d-flex flex-column gap-3">
          <p className="text-body-secondary mb-0">Your accounts on other platforms we work with.</p>
          <TradingViewRow />
          {membershipsEnabled && <MembershipAccountRows />}
        </CCardBody>
      </CCard>
    </CCol>
  );
}

function MembershipAccountRows() {
  const accounts = usePlatformAccounts();

  return (
    <>
      {(accounts.data ?? []).map((account) => (
        <AccountRow key={account.platformId} account={account} />
      ))}
    </>
  );
}

function TradingViewRow() {
  const account = useTradingViewAccount();

  if (!account.data) {
    return null;
  }

  const username = account.data.username || null;
  const locked = account.data.locked;

  return (
    <div className="d-flex flex-column gap-1">
      <div className="d-flex align-items-center gap-2">
        <div className="d-flex flex-column" style={{ minWidth: 0, flex: 1 }}>
          <span className="fw-semibold">TradingView</span>
          {username ? (
            <span className="small text-truncate">{username}</span>
          ) : (
            <span className="small text-body-secondary fst-italic">Not added</span>
          )}
        </div>
        {locked ? (
          <CIcon icon={cilLockLocked} className="text-body-secondary" aria-hidden />
        ) : (
          <Link
            to={routePaths.profileTradingView}
            state={{ returnTo: routePaths.profile }}
            className="btn btn-outline-primary btn-sm flex-shrink-0"
          >
            {username ? "Change TradingView username" : "Add TradingView username"}
          </Link>
        )}
      </div>
      {locked ? (
        <span className="small text-body-secondary">
          Locked. <Link to={routePaths.contact}>Contact us</Link> if it changes.
        </span>
      ) : (
        <span className="small text-body-secondary">
          Once you have TradingView Scripts Club, your username locks. After that, contact us to change it.
        </span>
      )}
    </div>
  );
}

function AccountRow({ account }: { account: PlatformAccountDto }) {
  const { showSuccess, showWarning } = useToast();
  const setAccount = useSetPlatformAccount();
  const removeAccount = useRemovePlatformAccount();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");

  const trimmed = value.trim();
  const busy = setAccount.isPending || removeAccount.isPending;

  const startEditing = () => {
    setValue(account.account ?? "");
    setEditing(true);
  };

  const save = async () => {
    try {
      await setAccount.mutateAsync({ platformId: account.platformId, account: trimmed });
      showSuccess(`Your ${account.name} account is saved.`);
      setEditing(false);
    } catch (err) {
      showWarning(err instanceof Error ? err.message : `We couldn't save your ${account.name} account.`);
    }
  };

  const remove = async () => {
    try {
      await removeAccount.mutateAsync(account.platformId);
      showSuccess(`Your ${account.name} account is removed.`);
    } catch (err) {
      showWarning(err instanceof Error ? err.message : `We couldn't remove your ${account.name} account.`);
    }
  };

  const badge = (
    <MembershipBadge
      membership={{ badgeColour: account.badgeColour, badgeLetters: account.badgeLetters, platformName: account.name }}
    />
  );

  if (editing) {
    return (
      <div className="d-flex flex-column gap-2">
        <label htmlFor={`platform-account-${account.platformId}`} className="d-flex align-items-center gap-2 fw-semibold">
          {badge}
          {account.accountPrompt ?? `Your ${account.name} account`}
        </label>
        <CFormInput
          id={`platform-account-${account.platformId}`}
          value={value}
          maxLength={MAX_PLATFORM_ACCOUNT_LENGTH}
          onChange={(e) => setValue(e.target.value)}
        />
        <div className="d-flex gap-2">
          <CButton color="primary" size="sm" onClick={save} disabled={busy || trimmed.length === 0}>
            {setAccount.isPending && <CSpinner size="sm" className="me-2" />}
            Save
          </CButton>
          <CButton color="secondary" variant="outline" size="sm" onClick={() => setEditing(false)} disabled={busy}>
            Cancel
          </CButton>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-1">
      <div className="d-flex align-items-center gap-2">
        {badge}
        <div className="d-flex flex-column" style={{ minWidth: 0, flex: 1 }}>
          <span className="fw-semibold">{account.name}</span>
          {account.account ? (
            <span className="small text-truncate">{account.account}</span>
          ) : (
            <span className="small text-body-secondary fst-italic">Not added</span>
          )}
        </div>
        {account.locked ? (
          <CIcon icon={cilLockLocked} className="text-body-secondary" aria-hidden />
        ) : (
          <div className="d-flex gap-2 flex-shrink-0">
            <CButton color="primary" variant="outline" size="sm" onClick={startEditing} disabled={busy}>
              {account.account ? "Change" : "Add"}
            </CButton>
            {account.account && (
              <CButton color="secondary" variant="outline" size="sm" onClick={remove} disabled={busy}>
                Remove
              </CButton>
            )}
          </div>
        )}
      </div>
      {account.locked && (
        <span className="small text-body-secondary">
          Locked to your {account.name} membership.{" "}
          <Link to={routePaths.contact}>Contact us</Link> to change it.
        </span>
      )}
    </div>
  );
}
